import { Component, inject, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges } from "@angular/core";
import { WebhookCommonComponent } from "../common.components";
import { BehaviorSubject, debounceTime, Observable, switchMap } from "rxjs";
import { HTTPService } from "src/app/services/http.service";
import { SearchProperties } from "../../webhook-interfaces/configuration";
import { DataSyncService } from "src/app/builder-screen-beta/services/data-sync.service";

@Component({
    selector: 'webhook-form-search',
    templateUrl: './webhook-form-search.component.html',
    styleUrl: './webhook-form-search.component.scss',
    standalone: false
})

export class WebhookFormSearchComponent extends WebhookCommonComponent implements OnInit, OnChanges, OnDestroy {
   
    searchChange$ = new BehaviorSubject('');
    optionList: {key:string, value:string}[] = [];
    selectedValue?: string;
    loading = false;
    private lastCondition?: string; // Rastreia a última condition usada

    // Campos derivados (field mappings): armazena linha completa da API por chave
    private rowDataMap = new Map<string, any>();

    onSearch(value:string): void {
        this.loading = true;
        this.field.searchProperties.condition='0=0'; // Limpa condition para não interferir na busca dinâmica
        this.searchChange$.next(value);
    }

    http: HTTPService = inject(HTTPService);
    private dataSyncService: DataSyncService | null = inject(DataSyncService, { optional: true });
    
    ngOnDestroy(): void {
        this.searchChange$.complete();
    }

    /**
     * Detecta mudanças no @Input() field (ex: searchProperties.condition alterada por dependências).
     * Quando a condition muda, limpa a lista de opções e re-dispara a busca.
     */
    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if (changes['field'] && !changes['field'].firstChange) {
            const newCondition = this.field?.searchProperties?.condition;
            if (newCondition !== this.lastCondition) {
                this.lastCondition = newCondition;
                // Limpar lista de opções e rowDataMap para não exibir dados stale
                this.optionList = [];
                this.rowDataMap.clear();
                // Re-disparar busca com string vazia para atualizar opções
                this.searchChange$.next('');
            }
        }
    }

    ngOnInit(): void {
        super.ngOnInit();
        // Guardar condition inicial
        this.lastCondition = this.field?.searchProperties?.condition;

        this.searchChange$
        .pipe(
            debounceTime(500),
            switchMap(data => this.getValue(data))
        )
        .subscribe((data:{key:string, value:string}[]) => {
            this.optionList = data;
            this.loading = false;
            this.cd.markForCheck();
        });
    }

    /**
     * Sobrescreve executeListener para emitir campos derivados (field mappings)
     * quando o usuário seleciona um item na pesquisa.
     */
    override executeListener(ob: any): void {
        super.executeListener(ob);
        this.emitFieldMappings(ob);
    }

    getValue(data:string): Observable<{key:string, value:string}[]> {
        
        const searchProps = this.field.searchProperties || {
            entityName: 'Mktplaces',
            fields: { keyField: 'CODMKTP', valueField: 'NAME' }
        };

        let ob = {
            entityName: searchProps.entityName,            
            condition: this.commomServices.applyVariables( this.applyVariables(searchProps, data), this.variables), 
            showPresentation:false,
            showRelationshipData:false,
            showRelationship:false,
            showTableInformation:false,
        }
        return this.http.post(ob, 'JsonBrokerServices/find', false, true, true).pipe(
            switchMap((response: any) => {
                if (response[searchProps.entityName] != null) {
                    let list = response[searchProps.entityName];
                    // Armazenar linhas completas para field mappings
                    this.rowDataMap.clear();
                    list.forEach((item: any) => {
                        this.rowDataMap.set(String(item[searchProps.fields.keyField]), item);
                    });
                    return [list.map(item => ({
                        key: item[searchProps.fields.keyField],
                        value: item[searchProps.fields.valueField]
                    }))];
                } else {
                    return [[]];
                }
            })
        );
    }

    applyVariables(fields: SearchProperties, data) {
        let condition = fields.condition!=null ? fields.condition + " AND " : '';
        condition = condition + "("+ fields.fields.keyField + ' LIKE \'%' + data + '%\'';
        condition = condition + ' OR ' + fields.fields.valueField + ' LIKE \'%' + data + '%\'' +")";
        return condition;
    }

    /**
     * Emite valores de campos derivados (field mappings) quando o usuário seleciona um item.
     * Para cada mapeamento configurado, extrai a coluna da linha selecionada e emite via fieldSync$.
     */
    private emitFieldMappings(selectedKey: any): void {
        if (!this.dataSyncService || selectedKey == null || selectedKey === '') return;

        const fieldMappings: any[] = (this.field as any)?.fieldMappings;
        if (!fieldMappings || fieldMappings.length === 0) return;

        const rowData = this.rowDataMap.get(String(selectedKey));
        if (!rowData) return;

        for (const mapping of fieldMappings) {
            const value = rowData[mapping.sourceColumn];
            if (value !== undefined) {
                // Emitir para que outros componentes com esse dataPath atualizem
                this.dataSyncService.emitFieldSync(
                    mapping.targetDataPath,
                    value,
                    'fieldMapping_' + this.fieldNew
                );
                // Também gravar no data store (para que a dependência capture o valor)
                this.dataSyncService.onFormFieldChange(mapping.targetDataPath, value);
            }
        }
    }
}

